import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Flame,
  Mail,
  Lock,
  User,
  Loader2,
  Gift,
  Bot,
  Brain,
  Zap,
  Skull,
  Dumbbell,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  KeyRound,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AppTheme, DEFAULT_THEME, BotIcon } from "@/types/evolve";
import { isDespiaNative } from "@/utils/despia";

const THEME_STORAGE_KEY = "evolve-app-theme";

type AuthMode = "login" | "signup" | "forgotPassword" | "resetPassword" | "confirmEmail";

// Map bot icon values to Lucide components
const BOT_ICON_COMPONENTS: Record<BotIcon, React.ComponentType<{ className?: string }>> = {
  flame: Flame,
  bot: Bot,
  brain: Brain,
  zap: Zap,
  skull: Skull,
  dumbbell: Dumbbell,
};

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get referral code and mode from URL - check FIRST before any state
  const referralCode = searchParams.get("ref");
  const urlMode = searchParams.get("mode");
  const isResetMode = urlMode === "reset";

  // Initialize mode based on URL - this ensures reset mode is set from the start
  const [mode, setMode] = useState<AuthMode>(() => {
    if (isResetMode) return "resetPassword";
    // Also check hash for recovery type (from email link redirect)
    const hash = window.location.hash;
    if (hash && hash.includes("type=recovery")) return "resetPassword";
    return "login";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [confirmationEmailSent, setConfirmationEmailSent] = useState(false);
  const [pendingConfirmationEmail, setPendingConfirmationEmail] = useState("");
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  // Read theme from localStorage
  const savedTheme = useMemo(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as AppTheme;
      }
    } catch {
      // ignore
    }
    return DEFAULT_THEME;
  }, []);

  const BotIconComponent = BOT_ICON_COMPONENTS[savedTheme.botIcon || "flame"];

  useEffect(() => {
    // If there's a referral code, default to signup mode
    if (referralCode) {
      setMode("signup");
    }
  }, [referralCode]);

  // Check for recovery tokens in URL hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("type=recovery")) {
      console.log("[Auth] Recovery token detected in hash, setting resetPassword mode");
      setMode("resetPassword");
    }
  }, []);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[Auth] Auth state change:", event, !!session, "isResetMode:", isResetMode);

      // Handle password recovery event - this fires when user clicks the reset link
      if (event === "PASSWORD_RECOVERY") {
        console.log("[Auth] Password recovery detected, setting resetPassword mode");
        setMode("resetPassword");
        setIsInitialized(true);
        return;
      }

      // If we're in reset mode (from URL or PASSWORD_RECOVERY event), DON'T redirect
      if (isResetMode) {
        console.log("[Auth] In reset mode, not redirecting");
        setIsInitialized(true);
        return;
      }

      if (session?.user) {
        // If this is a new signup with a referral code, process it via server-side function
        // Use setTimeout to avoid any async issues in the callback
        if (event === "SIGNED_IN" && referralCode) {
          setTimeout(() => {
            processReferralServerSide(referralCode).catch((err) => {
              console.error("[Auth] Referral processing error:", err);
              // Don't throw - allow navigation to continue
            });
          }, 0);
        }
        // Navigate to home - use replace to prevent back button issues
        // Use setTimeout to ensure state updates complete first
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 50);
      }
      setIsInitialized(true);
    });

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("[Auth] Initial session check:", !!session, "isResetMode:", isResetMode);

      // If we're in reset mode, ensure we stay in reset mode regardless of session
      if (isResetMode) {
        setMode("resetPassword");
        setIsInitialized(true);
        return;
      }

      // If user has session, redirect home
      if (session?.user) {
        navigate("/", { replace: true });
      }
      setIsInitialized(true);
    });

    return () => subscription.unsubscribe();
  }, [navigate, referralCode, isResetMode]);

  const processReferralServerSide = async (code: string) => {
    try {
      const { error } = await supabase.functions.invoke("process-referral", {
        body: { referralCode: code },
      });

      if (error) {
        console.error("Error processing referral:", error);
        return;
      }

      toast.success("Referral applied! You get 7 days free + 40% off!");
    } catch (error) {
      console.error("Error processing referral:", error);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast.error("Google sign-in failed. Please try again.");
        console.error("[Auth] Google sign-in error:", error);
      }
    } catch (err: any) {
      toast.error(err.message || "Google sign-in failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setAppleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("apple", {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast.error("Apple sign-in failed. Please try again.");
        console.error("[Auth] Apple sign-in error:", error);
      }
    } catch (err: any) {
      toast.error(err.message || "Apple sign-in failed");
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === "forgotPassword") {
      await handleForgotPassword();
      return;
    }

    if (mode === "resetPassword") {
      await handleResetPassword();
      return;
    }

    if (mode === "confirmEmail") {
      await handleResendConfirmation();
      return;
    }

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (mode === "signup" && !name) {
      toast.error("Please enter your name");
      return;
    }

    if (mode === "signup" && !agreedToTerms) {
      toast.error("Please agree to the Terms & Conditions and Privacy Policy");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      if (mode === "login") {
        // Wrap login in its own try-catch for better error isolation
        try {
          const { error, data } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          if (error) {
            // Handle specific login errors gracefully
            if (error.message.includes("Invalid login credentials")) {
              toast.error("Invalid email or password");
            } else if (error.message.includes("Email not confirmed")) {
              setPendingConfirmationEmail(email);
              setMode("confirmEmail");
            } else {
              toast.error(error.message || "Login failed");
            }
            setLoading(false);
            return;
          }
          
          // Login successful - the onAuthStateChange will handle navigation
          // Don't navigate here - let onAuthStateChange handle it
          // This prevents race conditions
        } catch (loginError: any) {
          console.error("[Auth] Login error:", loginError);
          toast.error(loginError.message || "An unexpected error occurred during login");
          setLoading(false);
          return;
        }
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              name,
              referred_by_code: referralCode || undefined,
            },
          },
        });
        if (error) throw error;

        // Check if email confirmation is required
        if (data?.user && !data.session) {
          // Check if this is a "fake" signup (user already exists)
          // When email enumeration protection is on, Supabase returns a user with empty identities
          if (!data.user.identities || data.user.identities.length === 0) {
            toast.error("This email is already registered. Try logging in instead.");
            setMode("login");
            return;
          }
          // User was created but session is null - email confirmation required
          setPendingConfirmationEmail(email);
          setMode("confirmEmail");
          toast.success("Please check your email to confirm your account");
        } else {
          toast.success("Account created! Welcome to Evolve.");
        }
      }
    } catch (error: any) {
      console.error("[Auth] Auth error:", error);
      if (error.message?.includes("User already registered")) {
        toast.error("This email is already registered. Try logging in.");
      } else if (error.message?.includes("Invalid login credentials")) {
        toast.error("Invalid email or password");
      } else if (error.message?.includes("Email not confirmed")) {
        setPendingConfirmationEmail(email);
        setMode("confirmEmail");
      } else {
        toast.error(error.message || "An error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      // Redirect to /auth - Supabase will trigger PASSWORD_RECOVERY event which sets resetPassword mode
      const currentDomain = window.location.origin;
      const resetUrl = `${currentDomain}/auth`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetUrl,
      });

      if (error) throw error;
      setResetEmailSent(true);
      toast.success("Check your email for the reset link");
    } catch (error: any) {
      console.error("[Auth] Error sending password reset:", error);
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      // Show success state
      setPasswordResetSuccess(true);
      toast.success("Password updated successfully!");

      // Sign out and redirect after a delay so user sees success
      setTimeout(async () => {
        await supabase.auth.signOut();
        setMode("login");
        setPassword("");
        setConfirmPassword("");
        setPasswordResetSuccess(false);
        navigate("/auth");
      }, 2000);
    } catch (error: any) {
      toast.error(error.message || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    const emailToResend = pendingConfirmationEmail || email;
    if (!emailToResend) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: emailToResend,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });
      if (error) throw error;
      setConfirmationEmailSent(true);
      toast.success("Confirmation email sent! Please check your inbox.");
    } catch (error: any) {
      toast.error(error.message || "Failed to resend confirmation email");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case "forgotPassword":
        return "Reset Password";
      case "resetPassword":
        return "Set New Password";
      case "confirmEmail":
        return "Confirm Email";
      case "signup":
        return "EVOLVE";
      default:
        return "EVOLVE";
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case "forgotPassword":
        return resetEmailSent ? "Check your email for a reset link." : "Enter your email to receive a reset link.";
      case "resetPassword":
        return "Enter your new password below.";
      case "confirmEmail":
        return "Please confirm your email to continue.";
      case "signup":
        return "Begin your transformation.";
      default:
        return "Welcome back, warrior.";
    }
  };

  // Show loading while checking auth state for reset mode
  if (isResetMode && !isInitialized) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-8">
        {/* Back button for forgot/reset password/confirm email */}
        {(mode === "forgotPassword" || mode === "resetPassword" || mode === "confirmEmail") && (
          <button
            type="button"
            onClick={() => {
              setMode("login");
              setResetEmailSent(false);
              setConfirmationEmailSent(false);
              setPassword("");
              setConfirmPassword("");
              setPendingConfirmationEmail("");
            }}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to login</span>
          </button>
        )}

        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full evolve-gradient flex items-center justify-center evolve-glow shadow-lg">
              <BotIconComponent className="w-8 h-8 text-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-black tracking-tight">{getTitle()}</h1>
          <p className="text-muted-foreground text-sm">{getSubtitle()}</p>
        </div>

        {/* Referral Banner */}
        {referralCode && mode === "signup" && (
          <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3">
            <Gift className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-emerald-400">Referral Bonus!</div>
              <div className="text-muted-foreground">7-day trial + 40% off first month</div>
            </div>
          </div>
        )}

        {/* Confirm Email View */}
        {mode === "confirmEmail" ? (
          <div className="text-center space-y-4">
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-6">
              <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-2">We've sent a confirmation link to</p>
              <p className="font-medium text-foreground">{pendingConfirmationEmail || email}</p>
              {confirmationEmailSent && <p className="text-sm text-emerald-400 mt-3">✓ Confirmation email resent!</p>}
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleResendConfirmation}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend confirmation email"}
            </Button>
            <p className="text-xs text-muted-foreground">Didn't receive it? Check your spam folder or try resending.</p>
          </div>
        ) : mode === "forgotPassword" && resetEmailSent ? (
          /* Forgot Password - Email Sent Success */
          <div className="text-center space-y-4">
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-6">
              <Mail className="w-12 h-12 text-primary mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">We've sent a password reset link to</p>
              <p className="font-medium text-foreground mt-1">{email}</p>
            </div>

            {/* Spam folder warning */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-left">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p className="font-medium text-amber-500">Check your spam folder!</p>
                  <p className="text-muted-foreground">
                    Reset emails can sometimes end up in spam, especially for Yahoo and Outlook users.
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleForgotPassword}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resend reset email"}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => {
                setResetEmailSent(false);
                setEmail("");
              }}
            >
              Try a different email
            </Button>
          </div>
        ) : passwordResetSuccess ? (
          /* Password Reset Success */
          <div className="text-center space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="font-medium text-emerald-400 text-lg">Password Updated!</p>
              <p className="text-sm text-muted-foreground mt-2">Redirecting you to login...</p>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Reset Password Mode Banner */}
            {mode === "resetPassword" && (
              <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 flex items-center gap-3 mb-2">
                <KeyRound className="w-5 h-5 text-primary flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-primary">Set Your New Password</p>
                  <p className="text-muted-foreground">Enter a new password below to complete the reset.</p>
                </div>
              </div>
            )}
            {/* Name field - signup only */}
            {mode === "signup" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-card border-border"
                />
              </div>
            )}

            {/* Email field - login, signup, forgotPassword */}
            {(mode === "login" || mode === "signup" || mode === "forgotPassword") && (
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-card border-border"
                />
              </div>
            )}

            {/* Password field - login, signup, resetPassword */}
            {(mode === "login" || mode === "signup" || mode === "resetPassword") && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder={mode === "resetPassword" ? "New password" : "Password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-card border-border"
                />
              </div>
            )}

            {/* Confirm Password - resetPassword only */}
            {mode === "resetPassword" && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-card border-border"
                />
              </div>
            )}

            {/* Forgot Password Link - login only */}
            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setMode("forgotPassword")}
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Terms & Conditions Checkbox - signup only */}
            {mode === "signup" && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                  I agree to the{" "}
                  <a
                    href="https://evolveaicoach.app/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://app.termly.io/policy-viewer/policy.html?policyUUID=1b15c119-9dba-4d37-8a3b-dba1079ae98a"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}

            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : mode === "login" ? (
                "Sign In"
              ) : mode === "signup" ? (
                "Create Account"
              ) : mode === "forgotPassword" ? (
                "Send Reset Link"
              ) : (
                "Update Password"
              )}
            </Button>

            {/* Social Sign-In - login and signup modes */}
            {(mode === "login" || mode === "signup") && (
              <>
                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or</span>
                  </div>
                </div>

                {!isDespiaNative() && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleSignIn}
                    disabled={googleLoading}
                  >
                    {googleLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    {googleLoading ? "Signing in..." : "Continue with Google"}
                  </Button>
                )}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleAppleSignIn}
                  disabled={appleLoading}
                >
                  {appleLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                  )}
                  {appleLoading ? "Signing in..." : "Continue with Apple"}
                </Button>
              </>
            )}
          </form>
        )}

        {/* Toggle between login/signup */}
        {(mode === "login" || mode === "signup") && (
          <p className="text-center text-sm text-muted-foreground">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="text-primary font-medium hover:underline"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        )}
      </div>
    </main>
  );
};

export default Auth;
